import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Stack, Tabs, Tab, Grid, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Alert,
  Checkbox, FormControlLabel, Snackbar, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { useAuthStore } from '../../store/authStore';
import { tokens } from '../../theme';
import { staffApi, Staff } from '../../services/staffService';
import StaffScheduleDialog from '../../components/StaffScheduleDialog';
import { apiClient } from '../../services/api';
import { useSalonContextStore } from '../../store/salonContextStore';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatTile, StatusPill, PersonAvatar } from '../../components/ui';
import { formatMoney, formatNumber, formatDuration, countLabel, plural } from '../../utils/format';

const DAY_NAMES = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
// Tydzien w grafiku zaczyna sie od poniedzialku; indeksy zostaja zgodne z dayOfWeek z API.
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const PROFILE_TABS = ['Usługi', 'Grafik pracy', 'Prowizje'];

const NAME_REQUIRED = 'Imię i nazwisko są wymagane';
const EMAIL_REQUIRED = 'E-mail jest wymagany';

interface SalonService {
  id: string;
  name: string;
  duration?: number;
  price?: number;
}

/** Sekcja w oknie: tytul, opcjonalny opis i pola. */
const DialogSection: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  first?: boolean;
  children: React.ReactNode;
}> = ({ title, description, first, children }) => (
  <Box sx={{ pt: first ? 0.5 : 2.5, pb: 2.5, borderTop: first ? 'none' : `1px solid ${tokens.line}` }}>
    <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4, mb: description ? 0.25 : 1.75 }}>
      {title}
    </Typography>
    {description && (
      <Typography component="div" sx={{ mb: 1.75, fontSize: '0.875rem', color: tokens.muted }}>{description}</Typography>
    )}
    {children}
  </Box>
);

const dialogSubtitleSx = { display: 'block', mt: 0.25, fontSize: '0.9375rem', fontWeight: 500, color: tokens.muted } as const;

// ─── ADD / EDIT STAFF DIALOG ─────────────────────────────
const StaffFormDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  staff?: Staff | null;
  salonId: string;
}> = ({ open, onClose, onSave, staff, salonId }) => {
  const isEdit = !!staff;
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', phone: '', bio: '', specialization: '', commissionRate: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (staff) {
      setForm({
        email: staff.user?.email || '',
        firstName: staff.firstName,
        lastName: staff.lastName,
        phone: staff.phone || '',
        bio: staff.bio || '',
        specialization: staff.specialization || '',
        commissionRate: staff.commissionRate ?? 0,
      });
    } else {
      setForm({ email: '', firstName: '', lastName: '', phone: '', bio: '', specialization: '', commissionRate: 0 });
    }
    setError('');
  }, [staff, open]);

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError(NAME_REQUIRED);
      return;
    }
    if (!isEdit && !form.email.trim()) {
      setError(EMAIL_REQUIRED);
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isEdit && staff) {
        await staffApi.update(staff.id, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
          bio: form.bio.trim() || undefined,
          specialization: form.specialization.trim() || undefined,
          commissionRate: form.commissionRate,
        });
      } else {
        await staffApi.add({
          salonId,
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
          bio: form.bio.trim() || undefined,
          specialization: form.specialization.trim() || undefined,
          commissionRate: form.commissionRate,
        });
      }
      onSave();
      onClose();
    } catch (err: any) {
      const details = err?.response?.data?.details;
      const firstDetail = Array.isArray(details) ? details[0]?.message : undefined;
      setError(firstDetail || err?.response?.data?.error || 'Nie udało się zapisać pracownika. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  // Bledy walidacji pokazujemy przy polach, bledy z serwera — nad formularzem.
  const nameMissing = error === NAME_REQUIRED;
  const emailMissing = error === EMAIL_REQUIRED;
  const firstNameError = nameMissing && !form.firstName.trim();
  const lastNameError = nameMissing && !form.lastName.trim();
  const emailError = emailMissing && !form.email.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edytuj dane pracownika' : 'Dodaj pracownika'}</DialogTitle>
      <DialogContent>
        {error && !nameMissing && !emailMissing && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <DialogSection first title="Dane kontaktowe">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Imię" fullWidth required value={form.firstName}
                error={firstNameError} helperText={firstNameError ? 'Podaj imię' : undefined}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Nazwisko" fullWidth required value={form.lastName}
                error={lastNameError} helperText={lastNameError ? 'Podaj nazwisko' : undefined}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </Grid>
            {!isEdit ? (
              <Grid item xs={12} sm={6}>
                <TextField label="E-mail" type="email" fullWidth required value={form.email}
                  error={emailError}
                  helperText={emailError ? 'Podaj adres e-mail' : 'Wyślemy na niego zaproszenie do konta'}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Grid>
            ) : form.email ? (
              <Grid item xs={12} sm={6}>
                <TextField label="E-mail" fullWidth disabled value={form.email}
                  helperText="Adres logowania — nie zmienisz go tutaj" />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6}>
              <TextField label="Telefon" type="tel" fullWidth value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+48 123 456 789" />
            </Grid>
          </Grid>
        </DialogSection>

        <DialogSection title="Praca w salonie">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Specjalizacja" fullWidth value={form.specialization}
                onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                placeholder="np. Starsza stylistka"
                helperText="Widoczna dla klientów przy rezerwacji online" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Prowizja od usług" type="number" fullWidth value={form.commissionRate}
                onChange={(e) => setForm({ ...form, commissionRate: Math.max(0, Math.min(100, Number(e.target.value))) })}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="Procent od wartości wykonanych usług" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="O pracowniku" fullWidth multiline minRows={3} value={form.bio}
                sx={{ '& .MuiInputBase-inputMultiline': { p: 0 } }}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="np. 8 lat doświadczenia, koloryzacja i upięcia ślubne"
                helperText="Klienci zobaczą ten opis przy wyborze pracownika" />
            </Grid>
          </Grid>
        </DialogSection>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isEdit ? 'Zapisz zmiany' : 'Dodaj pracownika'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ─── SERVICES ASSIGNMENT DIALOG ──────────────────────────
const ServicesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  staffId: string;
  staffName: string;
  assignedServiceIds: string[];
  salonServices: SalonService[];
}> = ({ open, onClose, onSave, staffId, staffName, assignedServiceIds, salonServices }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSelected(new Set(assignedServiceIds));
    setError('');
  }, [assignedServiceIds, open]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await staffApi.assignServices(staffId, Array.from(selected));
      onSave();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się zapisać usług. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Przypisz usługi
        <Box component="span" sx={dialogSubtitleSx}>{staffName}</Box>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {salonServices.length === 0 ? (
          <EmptyState compact icon={<ContentCutOutlinedIcon />} title="Brak usług w salonie"
            description="Najpierw dodaj usługi w cenniku (Usługi i magazyn → Usługi i cennik)." />
        ) : (
          <>
            <Typography variant="body2" sx={{ color: tokens.muted, mb: 1.5 }}>
              Zaznacz usługi, które wykonuje ten pracownik.
            </Typography>
            <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: '12px' }}>
              {salonServices.map((svc) => {
                const meta = [svc.duration ? formatDuration(svc.duration) : null, svc.price ? formatMoney(svc.price) : null]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <FormControlLabel
                    key={svc.id}
                    control={<Checkbox checked={selected.has(svc.id)} onChange={() => toggle(svc.id)} />}
                    label={
                      <Box sx={{ minWidth: 0, py: 0.75 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{svc.name}</Typography>
                        {meta && <Typography variant="caption" component="div">{meta}</Typography>}
                      </Box>
                    }
                    sx={{ display: 'flex', m: 0, pl: 0.75, pr: 1.5, '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.line}` } }}
                  />
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Zapisz usługi ({selected.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── DEACTIVATE CONFIRM DIALOG ───────────────────────────
const DeactivateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  staffName: string;
  loading: boolean;
}> = ({ open, onClose, onConfirm, staffName, loading }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Dezaktywować pracownika?</DialogTitle>
    <DialogContent>
      <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5, mb: 1.5, fontWeight: 600, color: tokens.ink }}>
        {staffName}
      </Box>
      <Typography sx={{ color: tokens.inkSoft, fontSize: '0.9375rem' }}>
        Pracownik nie będzie widoczny w kalendarzu i rezerwacjach. Możesz go później aktywować ponownie.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button variant="text" onClick={onClose} autoFocus>Anuluj</Button>
      <Button variant="contained" color="error" onClick={onConfirm} disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        Dezaktywuj pracownika
      </Button>
    </DialogActions>
  </Dialog>
);

const StatBox: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ bgcolor: tokens.surface, borderRadius: '12px', px: 1.75, py: 1.25, minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 750, fontVariantNumeric: 'tabular-nums', lineHeight: 1.35, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

// ═════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════
export const SalonStaffPage: React.FC = () => {
  const navigate = useNavigate();
  // Kartoteka jest widoczna takze dla kierownictwa i ksiegowosci — to dla nich
  // jedyna lista zespolu. Zmieniac dane moze jednak tylko wlasciciel, zgodnie
  // z tym, co i tak dopuszcza API (PUT /staff/... to SALON_OWNER i SUPER_ADMIN).
  const role = useAuthStore((st) => st.user?.role);
  const canManage = role === 'SALON_OWNER' || role === 'SUPER_ADMIN';
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [salonServices, setSalonServices] = useState<SalonService[]>([]);
  const [salonId, setSalonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [profileTab, setProfileTab] = useState(0);
  const [showInactive, setShowInactive] = useState(false);

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [editStaff, setEditStaff] = useState<Staff | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [snack, setSnack] = useState('');
  const [snackTone, setSnackTone] = useState<'success' | 'error'>('success');
  const { activeSalonId, loadSalons } = useSalonContextStore();

  const notify = (message: string, tone: 'success' | 'error' = 'success') => {
    setSnackTone(tone);
    setSnack(message);
  };

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  const loadData = useCallback(async () => {
    if (!activeSalonId) {
      setError('Nie wybrano salonu');
      setLoading(false);
      return;
    }

    // Salon z kontekstu dochodzi po chwili (loadSalons) — wtedy komunikat
    // „Nie wybrano salonu” z pierwszego renderu nie moze zostac na ekranie.
    setError('');
    try {
      const sid = activeSalonId;
      setSalonId(sid);

      const [staff, services] = await Promise.all([
        staffApi.listBySalon(sid, showInactive),
        apiClient.get(`/services/salon/${sid}`).then((r) => {
          const d = r.data;
          return Array.isArray(d) ? d : d.data || [];
        }).catch(() => []),
      ]);

      setStaffList(staff);
      setSalonServices(services);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  }, [showInactive, activeSalonId]);

  useEffect(() => { loadData(); }, [loadData]);

  const refreshStaff = async () => {
    if (!salonId) return;
    try {
      const staff = await staffApi.listBySalon(salonId, showInactive);
      setStaffList(staff);
      // Refresh selected staff detail if open
      if (selectedStaff) {
        const updated = await staffApi.getById(selectedStaff.id);
        setSelectedStaff(updated);
      }
    } catch { /* silent */ }
  };

  const handleDeactivate = async () => {
    if (!selectedStaff) return;
    setDeactivateLoading(true);
    try {
      await staffApi.remove(selectedStaff.id);
      notify('Pracownik dezaktywowany');
      setDeactivateOpen(false);
      setSelectedStaff(null);
      await refreshStaff();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Nie udało się dezaktywować pracownika. Spróbuj ponownie.', 'error');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleReactivate = async (staff: Staff) => {
    try {
      await staffApi.update(staff.id, { isActive: true });
      notify('Pracownik aktywowany');
      await refreshStaff();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Nie udało się aktywować pracownika. Spróbuj ponownie.', 'error');
    }
  };

  const snackbar = (
    <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert severity={snackTone} variant="filled" onClose={() => setSnack('')}>{snack}</Alert>
    </Snackbar>
  );

  if (loading) {
    return (
      <Box>
        <PageHeader breadcrumbs={[{ label: 'Zespół' }]} title="Pracownicy" description="Ładowanie zespołu…" />
        <LoadingState variant="cards" count={3} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader breadcrumbs={[{ label: 'Zespół' }]} title="Pracownicy" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // ─── PROFILE VIEW ─────────────────────────────────
  if (selectedStaff) {
    const bookingCount = selectedStaff._count?.bookings ?? 0;
    const assignedServices = selectedStaff.services || [];
    const schedules = selectedStaff.schedules || [];
    const workingDays = schedules.filter((s) => s.isWorking).length;
    const fullName = `${selectedStaff.firstName} ${selectedStaff.lastName}`;
    const commission = selectedStaff.commissionRate ?? 0;

    return (
      <Box>
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => { setSelectedStaff(null); setProfileTab(0); }}
          sx={{ mb: 1, ml: -1.5 }}>
          Wróć do listy
        </Button>

        <PageHeader
          breadcrumbs={[{ label: 'Zespół' }]}
          title={fullName}
          description={selectedStaff.specialization || 'Pracownik'}
          actions={
            <>
              {/* Wyniki, czas pracy i rozliczenia tej osoby — jedno wejscie
                  zamiast szukania jej po czterech stronach (uwaga klienta 4). */}
              <Button variant="outlined" endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/salon/staff/${selectedStaff.id}`)}>
                Wyniki i rozliczenia
              </Button>
              {canManage && (
                <>
                  <Button variant="outlined" startIcon={<EditOutlinedIcon />}
                    onClick={() => { setEditStaff(selectedStaff); setFormOpen(true); }}>
                    Edytuj dane
                  </Button>
                  {selectedStaff.isActive ? (
                    <Button variant="outlined" color="error" startIcon={<PersonOffOutlinedIcon />}
                      onClick={() => setDeactivateOpen(true)}>
                      Dezaktywuj
                    </Button>
                  ) : (
                    <Button variant="outlined" color="success" startIcon={<PersonOutlineIcon />}
                      onClick={() => handleReactivate(selectedStaff)}>
                      Aktywuj
                    </Button>
                  )}
                </>
              )}
            </>
          }
        />

        {/* Profile header */}
        <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack direction="row" gap={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <PersonAvatar firstName={selectedStaff.firstName} lastName={selectedStaff.lastName} seed={selectedStaff.id} size={56} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" columnGap={2} rowGap={0.75} flexWrap="wrap">
                <StatusPill label={selectedStaff.isActive ? 'Aktywny' : 'Nieaktywny'} tone={selectedStaff.isActive ? 'success' : 'neutral'} />
                {selectedStaff.user?.email && (
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                    <MailOutlineIcon sx={{ fontSize: 18, color: tokens.muted }} />
                    <Typography variant="body2" sx={{ color: tokens.inkSoft, wordBreak: 'break-all' }}>{selectedStaff.user.email}</Typography>
                  </Stack>
                )}
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <PhoneOutlinedIcon sx={{ fontSize: 18, color: tokens.muted }} />
                  <Typography variant="body2" sx={{ color: selectedStaff.phone ? tokens.inkSoft : tokens.muted }}>
                    {selectedStaff.phone || 'Brak telefonu'}
                  </Typography>
                </Stack>
              </Stack>
              {selectedStaff.bio && (
                <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 1.25, maxWidth: '72ch' }}>{selectedStaff.bio}</Typography>
              )}
            </Box>
          </Stack>

          {/* Quick stats */}
          <Box sx={{
            display: 'grid', gap: 1.5, mt: 2.5, pt: 2.5, borderTop: `1px solid ${tokens.line}`,
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          }}>
            <StatBox label="Wizyty" value={formatNumber(bookingCount)} />
            <StatBox label="Usługi" value={assignedServices.length} />
            <StatBox label="Prowizja od usług" value={`${formatNumber(commission)}%`} />
            <StatBox label="Dni pracy w tygodniu" value={workingDays} />
          </Box>
        </Paper>

        <Tabs value={profileTab} onChange={(_, v) => setProfileTab(v)} sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          {PROFILE_TABS.map((t) => <Tab key={t} label={t} />)}
        </Tabs>

        {/* Services tab */}
        {profileTab === 0 && (
          <SectionCard
            title="Przypisane usługi"
            subtitle={countLabel(assignedServices.length, 'usługa', 'usługi', 'usług')}
            padding="none"
            action={canManage ? <Button size="small" variant="outlined" onClick={() => setServicesOpen(true)}>Edytuj usługi</Button> : undefined}
          >
            {assignedServices.length === 0 ? (
              <EmptyState compact icon={<ContentCutOutlinedIcon />} title="Brak przypisanych usług"
                description={canManage
                  ? 'Kliknij „Edytuj usługi”, aby wybrać usługi, które wykonuje ta osoba.'
                  : 'Usługi pojawią się tutaj, gdy właściciel salonu je przypisze.'} />
            ) : (
              <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
                {assignedServices.map((ss) => (
                  <Stack key={ss.service.id} direction="row" alignItems="center" justifyContent="space-between" gap={2}
                    sx={{ px: { xs: 2, md: 2.5 }, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', minWidth: 0 }}>{ss.service.name}</Typography>
                    <Stack direction="row" alignItems="baseline" gap={2} sx={{ flexShrink: 0 }}>
                      {ss.service.duration ? (
                        <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, whiteSpace: 'nowrap' }}>
                          {formatDuration(ss.service.duration)}
                        </Typography>
                      ) : null}
                      {ss.service.price ? (
                        <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}>
                          {formatMoney(ss.service.price)}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        )}

        {/* Schedule tab */}
        {profileTab === 1 && (
          <SectionCard
            title="Grafik pracy"
            subtitle={`${countLabel(workingDays, 'dzień', 'dni', 'dni')} pracy w tygodniu`}
            padding="none"
            action={canManage ? <Button size="small" variant="outlined" onClick={() => setScheduleOpen(true)}>Edytuj grafik</Button> : undefined}
          >
            <TableContainer>
              <Table sx={{ minWidth: 340 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Dzień</TableCell>
                    <TableCell>Godziny</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DAY_ORDER.map((dayIdx) => {
                    const sch = schedules.find((s) => s.dayOfWeek === dayIdx);
                    const working = sch?.isWorking ?? false;
                    return (
                      <TableRow key={dayIdx}>
                        <TableCell sx={{ fontWeight: 600 }}>{DAY_NAMES[dayIdx]}</TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: working ? tokens.ink : tokens.muted, whiteSpace: 'nowrap' }}>
                          {working ? `${sch!.startTime}–${sch!.endTime}` : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <StatusPill label={working ? 'Pracuje' : 'Wolne'} tone={working ? 'success' : 'neutral'} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        )}

        {/* Commissions tab */}
        {profileTab === 2 && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
            <StatTile
              label="Prowizja od usług"
              value={formatNumber(commission)}
              unit="%"
              progress={commission}
              hint="Procent od wartości wykonanych usług"
            />
            <StatTile
              label="Łączna liczba wizyt"
              value={formatNumber(bookingCount)}
              hint="Wszystkie wizyty w historii"
            />
          </Box>
        )}

        {/* Dialogs */}
        <StaffFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditStaff(null); }}
          onSave={() => { refreshStaff(); notify('Zapisano zmiany'); }} staff={editStaff} salonId={salonId} />
        <StaffScheduleDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)}
          onSave={() => { refreshStaff(); notify('Grafik zapisany'); }}
          staffId={selectedStaff.id} staffName={fullName}
          currentSchedules={schedules} />
        <ServicesDialog open={servicesOpen} onClose={() => setServicesOpen(false)}
          onSave={() => { refreshStaff(); notify('Usługi zaktualizowane'); }}
          staffId={selectedStaff.id} staffName={fullName}
          assignedServiceIds={(assignedServices).map((s) => s.service.id)}
          salonServices={salonServices} />
        <DeactivateDialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}
          onConfirm={handleDeactivate} staffName={fullName}
          loading={deactivateLoading} />
        {snackbar}
      </Box>
    );
  }

  // ─── STAFF LIST VIEW ──────────────────────────────
  const activeCount = staffList.filter((s) => s.isActive).length;
  const inactiveCount = staffList.length - activeCount;
  const listDescription = staffList.length
    ? `${countLabel(activeCount, 'osoba', 'osoby', 'osób')} w zespole${inactiveCount ? ` · ${inactiveCount} ${plural(inactiveCount, 'nieaktywna', 'nieaktywne', 'nieaktywnych')}` : ''}`
    : 'Kartoteka zespołu: dane kontaktowe, usługi, grafik i prowizje.';

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }]}
        title="Pracownicy"
        description={listDescription}
        actions={
          <>
            <FormControlLabel
              control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
              label="Pokaż nieaktywnych"
              sx={{ mr: 1 }}
            />
            {canManage && (
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => { setEditStaff(null); setFormOpen(true); }}>
                Dodaj pracownika
              </Button>
            )}
          </>
        }
      />

      {staffList.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<PeopleAltOutlinedIcon />}
            title="Nie ma jeszcze pracowników"
            description={canManage
              ? 'Dodaj pierwszą osobę do zespołu — dostanie zaproszenie e-mail do swojego konta.'
              : 'Pracownicy pojawią się tutaj, gdy właściciel salonu doda ich do zespołu.'}
          />
        </SectionCard>
      ) : (
        <Box sx={{
          display: 'grid', gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
        }}>
          {staffList.map((member) => {
            const bookingCount = member._count?.bookings ?? 0;
            const serviceCount = member.services?.length ?? 0;
            const workingDays = (member.schedules || []).filter((s) => s.isWorking).length;
            const open = () => setSelectedStaff(member);

            return (
              <Paper
                key={member.id}
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                sx={{
                  p: 2.25, height: '100%', display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer',
                  bgcolor: member.isActive ? tokens.paper : tokens.surface,
                  transition: 'border-color .15s ease, box-shadow .15s ease',
                  '&:hover': { borderColor: tokens.lineStrong, boxShadow: 3 },
                  '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <PersonAvatar firstName={member.firstName} lastName={member.lastName} seed={member.id} size={48}
                    sx={{ opacity: member.isActive ? 1 : 0.6 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.35 }}>
                      {member.firstName} {member.lastName}
                    </Typography>
                    <Typography noWrap variant="body2" sx={{ color: tokens.muted }}>
                      {member.specialization || 'Pracownik'}
                    </Typography>
                  </Box>
                  {!member.isActive && <StatusPill label="Nieaktywny" tone="neutral" size="small" />}
                  <ChevronRightIcon sx={{ color: tokens.subtle, flexShrink: 0 }} />
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', borderTop: `1px solid ${tokens.line}`, pt: 1.75, mt: 'auto' }}>
                  {([['Wizyty', bookingCount], ['Usługi', serviceCount], ['Dni w tygodniu', workingDays]] as const).map(([label, value], i) => (
                    <Box key={label} sx={{ minWidth: 0, pl: i ? 1.5 : 0, borderLeft: i ? `1px solid ${tokens.line}` : 'none' }}>
                      <Typography sx={{ fontSize: '1.125rem', fontWeight: 750, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(value)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: tokens.muted }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Dialogs */}
      <StaffFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditStaff(null); }}
        onSave={() => { loadData(); notify(editStaff ? 'Zapisano zmiany' : 'Pracownik dodany — wysłaliśmy zaproszenie e-mail'); }}
        staff={editStaff} salonId={salonId} />
      {snackbar}
    </Box>
  );
};
